import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { Zap, Phone, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

const CATEGORY_ORDER = [
  'EV Chargers', 'Panel Upgrades', 'Electrical Planning Reports',
  'Generators', 'Residential Electrical', 'Commercial Electrical', 'general'
];

const CATEGORY_META = {
  'EV Chargers': { label: 'EV Charger Installation', color: 'text-electric-teal', borderColor: 'border-electric-teal/20' },
  'Panel Upgrades': { label: 'Panel Upgrades & Service Upgrades', color: 'text-blue-400', borderColor: 'border-blue-500/20' },
  'Electrical Planning Reports': { label: 'Electrical Planning Reports (EPR)', color: 'text-purple-400', borderColor: 'border-purple-500/20' },
  'Generators': { label: 'Generator Installation', color: 'text-amber-400', borderColor: 'border-amber-500/20' },
  'Residential Electrical': { label: 'Residential Electrical', color: 'text-green-400', borderColor: 'border-green-500/20' },
  'Commercial Electrical': { label: 'Commercial Electrical', color: 'text-cyan-400', borderColor: 'border-cyan-500/20' },
  'general': { label: 'General Questions', color: 'text-white/60', borderColor: 'border-white/10' },
};

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [openItems, setOpenItems] = useState({});
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const response = await api.getFaqs();
        setFaqs(response.data);
      } catch (error) {
        console.error('Error loading FAQs:', error);
      }
    };
    loadFaqs();
  }, []);

  // Set meta tags + FAQ schema
  useEffect(() => {
    document.title = 'FAQ — Electrical Services Victoria BC | Island Coastal Electric';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.setAttribute('name', 'description'); document.head.appendChild(meta); }
    meta.setAttribute('content', 'Frequently asked questions about EV chargers, panel upgrades, EPR inspections, generators, and electrical services in Victoria, Langford, and Vancouver Island.');

    if (faqs.length > 0) {
      let script = document.getElementById('faq-page-schema');
      if (!script) { script = document.createElement('script'); script.id = 'faq-page-schema'; script.type = 'application/ld+json'; document.head.appendChild(script); }
      script.textContent = JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({ "@type": "Question", "name": f.question, "acceptedAnswer": { "@type": "Answer", "text": f.answer } }))
      });
    }
    return () => { document.getElementById('faq-page-schema')?.remove(); };
  }, [faqs]);

  const toggleItem = (id) => setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));

  // Group FAQs by category
  const grouped = faqs.reduce((acc, faq) => {
    const cat = faq.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {});

  const sortedCategories = CATEGORY_ORDER.filter(cat => grouped[cat]?.length > 0);

  return (
    <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32" data-testid="faq-page">
      {/* Hero */}
      <section className="py-20 bg-secondary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto">
            <span className="text-electric-teal font-bold uppercase tracking-widest text-sm">FAQ</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mt-4 mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-white/60 text-lg">
              Answers to common questions about our electrical services — cost, timing, permits, brands, and inspections.
            </p>
          </motion.div>

          {/* Quick Jump Links */}
          <div className="flex flex-wrap justify-center gap-2 mt-10">
            {sortedCategories.map(cat => {
              const meta = CATEGORY_META[cat] || CATEGORY_META.general;
              return (
                <a key={cat} href={`#faq-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-full bg-white/5 border ${meta.borderColor} ${meta.color} text-xs font-semibold hover:bg-white/10 transition-colors`}
                >
                  {meta.label} ({grouped[cat]?.length})
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Groups */}
      <section className="py-16 bg-primary-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {sortedCategories.map(cat => {
            const meta = CATEGORY_META[cat] || CATEGORY_META.general;
            const catFaqs = (grouped[cat] || []).sort((a, b) => (a.order || 0) - (b.order || 0));
            return (
              <div key={cat} id={`faq-${cat.toLowerCase().replace(/\s+/g, '-')}`} data-testid={`faq-group-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
                <h2 className={`text-xl font-bold ${meta.color} mb-6 flex items-center gap-2`}>
                  <span className={`w-2 h-2 rounded-full bg-current`} />
                  {meta.label}
                </h2>
                <div className="space-y-3">
                  {catFaqs.map((faq) => (
                    <div key={faq.id} className={`bg-secondary-dark rounded-lg border ${openItems[faq.id] ? meta.borderColor : 'border-white/5'} overflow-hidden transition-colors`}>
                      <button onClick={() => toggleItem(faq.id)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left group"
                        aria-expanded={!!openItems[faq.id]}
                        data-testid={`faq-toggle-${faq.id}`}
                      >
                        <span className="text-white font-medium pr-4 group-hover:text-electric-teal transition-colors text-sm md:text-base">
                          {faq.question}
                        </span>
                        {openItems[faq.id] ? <ChevronUp className="w-5 h-5 text-electric-teal flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-white/30 flex-shrink-0" />}
                      </button>
                      {openItems[faq.id] && (
                        <div className="px-6 pb-5">
                          <p className="text-white/60 text-sm leading-relaxed mb-4">{faq.answer}</p>
                          {/* Next Step Links */}
                          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
                            {faq.service_link && (
                              <Link to={faq.service_link} className="inline-flex items-center gap-1.5 text-electric-teal text-xs font-semibold hover:text-white transition-colors">
                                Learn more <ArrowRight className="w-3 h-3" />
                              </Link>
                            )}
                            {faq.city_link && (
                              <Link to={faq.city_link} className="inline-flex items-center gap-1.5 text-white/60 text-xs hover:text-electric-teal transition-colors">
                                Get a quote in your city <ArrowRight className="w-3 h-3" />
                              </Link>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-electric-teal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-4">Still Have Questions?</h2>
          <p className="text-primary-dark/70 text-lg mb-8">Our team is here to help. Call, email, or book a free estimate.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="faq-contact-btn">
              <Button className="bg-primary-dark text-white font-bold uppercase tracking-wider px-8 py-5 text-lg hover:bg-secondary-dark transition-all">Contact Us</Button>
            </Link>
            <a href="tel:7789602377" data-testid="faq-call-btn">
              <Button variant="outline" className="border-primary-dark text-primary-dark font-bold uppercase tracking-wider px-8 py-5 text-lg hover:bg-primary-dark/10 transition-all">
                <Phone className="w-5 h-5 mr-2" /> 778.960.2377
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
